// containerBackground.js — pure model + CSS resolution for the Container block's
// Background control (Image / GIF · Color · Gradient). Bucket D / T3 loose end
// (Gordon 2026-07-06; deck concepts/edit-wheel-flow/44a-44b).
//
// REUSE, NOT DUPLICATION (feature-completeness law): the gradient/image CSS is
// delegated to the app's canonical `buildSegmentFillCss`
// (Shared/SegmentStyling/logic/segmentFill.js), which itself delegates gradient
// serialisation to the secured, tested `gradientBuilder/gradientCss.js`. This
// module adds ONLY the container-specific model (a 3-type subset of the segment
// fill — no pattern) and the legacy-vs-fill RESOLUTION the renderer needs so a
// plain solid-color container (every existing wheel, and the Color type) keeps
// its unchanged `background` path and is byte-identical when the flag is OFF.
//
// PURE: no React, no DOM, no I/O — the risky mapping (a wrong string silently
// corrupts a user's container background) is unit-tested in isolation.

import { buildSegmentFillCss } from "../../Shared/SegmentStyling/logic/segmentFill";
import { defaultGradientState } from "../../../gradientBuilder/gradientCss";

/**
 * The container Background types, in the deck's selector order:
 * Image / GIF · Color · Gradient (44a). "image" covers both a still image and a
 * GIF — the universal picker's `isGifAllowed` handles the GIF case, and both are
 * stored as an image url (a container is not an open/closed wedge, so the
 * segment GIF poster/open contract does not apply).
 */
export const CONTAINER_FILL_TYPES = ["image", "color", "gradient"];

export const CONTAINER_FILL_TYPE_LABEL = {
  image: "Image / GIF",
  color: "Color",
  gradient: "Gradient",
};

/** A fresh gradient fill seeded from the canonical default gradient state. */
export const defaultGradientFill = () => ({
  type: "gradient",
  gradient: defaultGradientState(),
});

/** A fresh, empty image fill (renders the placeholder until a url is picked). */
export const defaultImageFill = () => ({ type: "image", image: {} });

/**
 * Coerce any stored value into a valid STRUCTURED container fill, or null.
 *
 * Returns null for undefined / non-object / the "color" type / anything invalid —
 * null means "use the legacy solid-color `background` path" (existing containers
 * and the Color type both take that path, so they stay byte-identical).
 *
 * @param {*} raw stored `containerFill` node prop
 * @returns {{type:'gradient',gradient:object}|{type:'image',image:object}|null}
 */
export function normalizeContainerFill(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (raw.type === "gradient") {
    const gradient =
      raw.gradient && Array.isArray(raw.gradient.stops)
        ? raw.gradient
        : defaultGradientState();
    return { type: "gradient", gradient };
  }
  if (raw.type === "image") {
    const image = raw.image && typeof raw.image === "object" ? raw.image : {};
    return { type: "image", image };
  }
  // "pattern" — a finished CSS `background` value produced by the SHARED
  // backgroundPatterns panel (buildPatternCss). ADDITIVE and deliberately NOT in
  // CONTAINER_FILL_TYPES: the Container block's own 3-tab selector never offers
  // it and therefore can never create one, so Container is untouched. Opt-in
  // surfaces (the Title Bar's deck-15 Patterns tab) pass it explicitly.
  if (raw.type === "pattern") {
    return typeof raw.css === "string" && raw.css.trim()
      ? { type: "pattern", css: raw.css }
      : null;
  }
  return null;
}

/** True when the stored fill is a structured (gradient/image) background. */
export function isStructuredFill(raw) {
  return normalizeContainerFill(raw) !== null;
}

/**
 * The type the UI selector should show for a stored fill: the structured type
 * when present, else "color" (the legacy solid-color path).
 */
export function fillTypeOf(raw) {
  const fill = normalizeContainerFill(raw);
  return fill ? fill.type : "color";
}

/**
 * The ONE entry point the renderer calls to decide how to paint the container
 * background.
 *
 *  - `{ mode: "legacy" }`  → use the container's existing solid-color
 *    `background` expression (unchanged; existing containers, the Color type, or
 *    any invalid fill). This is what keeps the OFF path / plain containers
 *    byte-identical.
 *  - `{ mode: "fill", css }` → paint `css` (a gradient string or a
 *    `url(...) cover` image value) built by the canonical buildSegmentFillCss.
 *
 * @param {*} raw stored `containerFill` node prop
 * @returns {{mode:'legacy'}|{mode:'fill', css:string}}
 */
export function resolveContainerBackground(raw) {
  const fill = normalizeContainerFill(raw);
  if (!fill) return { mode: "legacy" };
  // A pattern fill is ALREADY a finished CSS `background` value (the shared
  // pattern panel builds and validates it), so it bypasses buildSegmentFillCss
  // rather than being re-serialised through a model that has no pattern type.
  if (fill.type === "pattern") return { mode: "fill", css: fill.css };
  return { mode: "fill", css: buildSegmentFillCss(fill) };
}

const containerBackground = {
  CONTAINER_FILL_TYPES,
  CONTAINER_FILL_TYPE_LABEL,
  defaultGradientFill,
  defaultImageFill,
  normalizeContainerFill,
  isStructuredFill,
  fillTypeOf,
  resolveContainerBackground,
};

export default containerBackground;
